import { ConsentAttestControl } from '@/components/session/ConsentAttestControl';
import type { AttestDecision } from '@/components/session/ConsentAttestControl.types';
import type { DisplayedConsentStatus } from '@/components/session/ConsentStatusPill.types';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/hooks';
import type { UsePatientSearchReturn } from '@/hooks/usePatientSearch';
import { cn } from '@/lib/utils';
import type { Patient } from '@/types/session';
import { useMutation } from '@tanstack/react-query';
import { Check, ChevronDown, X } from 'lucide-react';
import { useState } from 'react';

// Border/background accents for the patient-select row itself — not part of
// the shared pill, which only covers the icon/label.
const rowAccentClasses: Record<DisplayedConsentStatus, { borderClass: string; pillBgClass: string }> = {
  active: { borderClass: 'border-l-green-500', pillBgClass: 'bg-green-50/60 hover:bg-green-50' },
  rejected: { borderClass: 'border-l-red-500', pillBgClass: 'bg-red-50/60 hover:bg-red-50' },
  not_viewed: { borderClass: 'border-l-amber-500', pillBgClass: 'bg-amber-50/60 hover:bg-amber-50' },
  error: { borderClass: 'border-l-gray-400', pillBgClass: 'bg-gray-50/60 hover:bg-gray-50' },
};

const getRowAccent = (status: Patient['consentStatus']) =>
  status && status !== 'unavailable' ? rowAccentClasses[status] : undefined;

const getConsentBorderClass = (status: Patient['consentStatus']): string => getRowAccent(status)?.borderClass ?? '';

const getConsentPillBgClass = (status: Patient['consentStatus']): string => getRowAccent(status)?.pillBgClass ?? '';

export const PatientSearchSelect = ({
  selectedPatient,
  setSelectedPatient,
  clearSelectedPatient,
  recentPatients,
  searchQuery,
  setSearchQuery,
  searchResults,
  isSearching,
  selectionNotice,
  allowClear = true,
}: UsePatientSearchReturn & { selectionNotice?: string | false; allowClear?: boolean }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  const handleSelectPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setOpen(false);
    setSearchQuery('');
    if (selectionNotice !== false) {
      toast({
        title: 'Sjúklingur valinn',
        description: selectionNotice ?? `${patient.registryName || patient.name} hefur verið tengdur við lotuna.`,
      });
    }
  };

  const attestMutation = useMutation({
    mutationFn: async ({ patientSsn, decision }: { patientSsn: string; decision: AttestDecision }) => {
      const response = await fetch(`/api/patients/attest-consent/${patientSsn}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision }),
      });
      if (!response.ok) {
        throw new Error('Attestation failed');
      }
      return response.json();
    },
    onSuccess: (_data, variables) => {
      const newStatus = variables.decision === 'permit' ? 'active' : 'rejected';
      if (selectedPatient) {
        // setSelectedPatient already invalidates ['patients', 'lookup', id] —
        // a second invalidation here would cause a duplicate refetch.
        setSelectedPatient({ ...selectedPatient, consentStatus: newStatus });
      }
    },
  });

  // Show search results if searching, otherwise show recent patients
  const displayPatients = searchQuery ? searchResults : recentPatients;
  const groupHeading = searchQuery ? 'Sjúklingar' : 'Síðustu uppflettingar';

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-expanded={open}
            className={cn(
              'flex min-h-14 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background transition-colors sm:min-h-12 sm:px-3',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              !selectedPatient && 'cursor-pointer text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              getRowAccent(selectedPatient?.consentStatus) && 'border-l-4 sm:border-l-[6px]',
              getConsentBorderClass(selectedPatient?.consentStatus),
              getConsentPillBgClass(selectedPatient?.consentStatus)
            )}
          >
            {selectedPatient ? (
              <div className="flex min-w-0 flex-1 flex-col items-start gap-0 overflow-hidden leading-tight">
                <span className="w-full truncate text-left font-semibold text-foreground text-sm sm:text-base">
                  {selectedPatient.registryName || selectedPatient.name}
                </span>
                <span className="text-left text-muted-foreground text-xs">{selectedPatient.ssn}</span>
              </div>
            ) : (
              <span>Velja sjúkling...</span>
            )}
            <div className="flex shrink-0 items-center">
              {selectedPatient?.consentStatus && (
                <ConsentAttestControl
                  status={selectedPatient.consentStatus}
                  isAttesting={attestMutation.isPending}
                  onAttest={(decision) => attestMutation.mutateAsync({ patientSsn: selectedPatient.id, decision })}
                />
              )}
              {selectedPatient && allowClear ? (
                <button
                  type="button"
                  className="rounded p-0.5 hover:bg-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSelectedPatient();
                  }}
                  aria-label="Hreinsa val"
                >
                  <X className="h-4 w-4 cursor-pointer opacity-50 hover:opacity-100" />
                </button>
              ) : (
                <ChevronDown className="ml-1 h-4 w-4 opacity-50" />
              )}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false} value={displayPatients[0]?.id ?? ''}>
            <CommandInput
              placeholder="Leita að sjúklingi (Kennitala)..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isSearching ? (
                <div className="p-4 text-center text-muted-foreground text-sm">Leita...</div>
              ) : searchQuery && searchResults.length === 0 ? (
                <CommandEmpty>Enginn sjúklingur fannst.</CommandEmpty>
              ) : displayPatients.length > 0 ? (
                <CommandGroup heading={groupHeading}>
                  {displayPatients.map((patient) => (
                    <CommandItem
                      key={patient.id}
                      value={patient.id}
                      onSelect={() => handleSelectPatient(patient)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn('mr-2 h-4 w-4', selectedPatient?.id === patient.id ? 'opacity-100' : 'opacity-0')}
                      />
                      <div className="flex flex-col">
                        <span className="font-medium">{patient.registryName || patient.name}</span>
                        <span className="text-muted-foreground text-xs">{patient.ssn}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : !searchQuery && recentPatients.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground text-sm">Sláðu inn kennitölu til að leita</div>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
};
